import { bboxPolygon, intersect } from '@turf/turf'
import L from 'leaflet'
import { useEffect, useRef } from 'react'

import { FieldInfo } from '../contexts/region-context'
import { MapMode } from '../region-map'

interface Feature {
  properties: { id: string }
}

interface Props {
  mapRef: React.RefObject<L.Map | null>
  mapMode: MapMode
  features: Feature[]
  regionFieldsInfo: FieldInfo[]
  isPerimetersVisible: boolean
  onMultipleFieldsSelection: (fieldIds: string[]) => void
}

const MIN_DRAG_DISTANCE = 5

export const useMultipleFieldSelection = ({
  mapRef,
  mapMode,
  features,
  regionFieldsInfo,
  isPerimetersVisible,
  onMultipleFieldsSelection,
}: Props) => {
  const state = useRef<{
    active: boolean
    startLatLng: L.LatLng | null
    startPixel: { x: number; y: number } | null
    rect: L.Rectangle | null
    moved: boolean
  }>({
    active: false,
    startLatLng: null,
    startPixel: null,
    rect: null,
    moved: false,
  })

  const reset = () => {
    const map = mapRef.current
    if (map && state.current.rect) map.removeLayer(state.current.rect)
    state.current = {
      active: false,
      startLatLng: null,
      startPixel: null,
      rect: null,
      moved: false,
    }
  }

  const processFields = (bounds: L.LatLngBounds) => {
    if (!isPerimetersVisible || !features.length) return

    const polygon = bboxPolygon([
      bounds.getWest(),
      bounds.getSouth(),
      bounds.getEast(),
      bounds.getNorth(),
    ])

    const selected = features
      .map((f) => {
        const id = f.properties?.id
        const field = regionFieldsInfo.find((r) => r.id === id)
        if (!id || !field?.diagnoses_count || field.diagnoses_count <= 1)
          return null
        try {
          return intersect(polygon, f as any) ? id : null
        } catch {
          return null
        }
      })
      .filter((id): id is string => id !== null)

    if (selected.length) onMultipleFieldsSelection(selected)
  }
  const map = mapRef.current
  const onMouseDown = (e: L.LeafletMouseEvent) => {
    if (e.originalEvent.button !== 0) return
    state.current = {
      active: true,
      startLatLng: e.latlng,
      startPixel: { x: e.originalEvent.clientX, y: e.originalEvent.clientY },
      rect: null,
      moved: false,
    }
  }

  const onMouseMove = (e: L.LeafletMouseEvent) => {
    if (!map) return
    const s = state.current
    if (!s.active || !s.startLatLng || !s.startPixel) return

    const dx = e.originalEvent.clientX - s.startPixel.x
    const dy = e.originalEvent.clientY - s.startPixel.y
    if (Math.sqrt(dx * dx + dy * dy) < MIN_DRAG_DISTANCE) return

    s.moved = true
    if (s.rect) map.removeLayer(s.rect)

    s.rect = L.rectangle(L.latLngBounds(s.startLatLng, e.latlng), {
      color: '#3388ff',
      weight: 2,
      fillOpacity: 0.2,
    })
    map.addLayer(s.rect)
  }

  const onMouseUp = () => {
    const s = state.current
    if (!s.active) return reset()
    if (s.moved && s.rect) processFields(s.rect.getBounds())
    reset()
    if (s.moved) {
      const blockClick = (event: Event) => {
        event.stopPropagation()
        document.removeEventListener('click', blockClick, true)
      }
      document.addEventListener('click', blockClick, true)
    }
  }
  useEffect(() => {
    if (!map || mapMode !== 'multipleSelection') return

    map.on('mousedown', onMouseDown)
    map.on('mousemove', onMouseMove)
    map.on('mouseup', onMouseUp)
    document.addEventListener('mouseup', reset)

    return () => {
      map.off('mousedown', onMouseDown)
      map.off('mousemove', onMouseMove)
      map.off('mouseup', onMouseUp)
      document.removeEventListener('mouseup', reset)
      reset()
    }
  }, [mapMode, features, regionFieldsInfo, isPerimetersVisible])

  return { reset, isDrawing: state.current.active }
}
